/**
 * REGISTRE DES TYPES DE PAGE.
 *
 * Ce que chaque type est, ce qu'il porte comme champs vides à la création, et
 * quels onglets le composeur ouvre pour lui. Un seul fichier, sur le modèle de
 * `stepKinds.js` : ces trois réponses étaient dispersées entre le formulaire,
 * l'aperçu et le graphe, et ajouter un type obligeait à les retrouver.
 */

import { CUSTOM_PAGES, customPageById } from "../graph_pages/customPages";

export const PAGE_TYPES = [
  "context", "mosaic", "one-choice", "score",
  "command_control", "two_doughnuts_graphs", "territory_intro", "custom",
];

export const PAGE_TYPE_INFO = {
  context: {
    label: "Page de contexte",
    color: "#6366f1",
    icon: "📋",
    // `short` : la phrase du choix de type, à la création. Elle dit ce que la
    // page FAIT FAIRE au participant, en une ligne lisible dans une carte —
    // `description` est le texte de l'inspecteur, trop long pour une grille.
    short: "Raconter, illustrer, faire une transition. Aucune décision.",
    description:
      "Affiche du contenu informatif : image, vidéo, audio et/ou texte. Aucune décision n'est prise ici. Utilisée pour introduire une phase, montrer les conséquences d'un choix, ou donner du contexte narratif entre les rounds.",
    tips: [
      "Choisissez d'abord un format (Diapo, Média et panneau, Texte seul, Plein cadre, Comparaison) : il décide de la mise en page et des emplacements à remplir.",
      "« Diapo » est la forme la plus courante : un visuel préparé dans Canva, projeté entier.",
      "Une bande son peut accompagner tous les formats sauf la comparaison ; déposez le fichier, l'URL n'est plus à saisir à la main.",
    ],
  },
  mosaic: {
    label: "Page de décision multiple (Mosaïque)",
    color: "#f59e0b",
    icon: "🧩",
    short: "Choisir plusieurs mesures dans une grille, avec un budget.",
    description:
      "Les participants choisissent librement plusieurs décisions parmi une liste, dans la limite de leur budget (metric défini dans contraintes). Chaque décision a un coût et des impacts sur les scores. L'ordre des décisions dans la liste détermine leur position dans la grille.",
    tips: [
      "Définissez 'display_score_decisions' pour afficher le coût de chaque décision.",
      "Utilisez 'list_additional_indicators' pour montrer des métriques radar en bas de page.",
      "Les conséquences générales s'ajoutent à la pile après la page, quel que soit le choix.",
    ],
  },
  "one-choice": {
    label: "Page de choix unique",
    color: "#ef4444",
    icon: "☝️",
    short: "Trancher : une seule option parmi 2 à 4.",
    description:
      "Les participants doivent choisir exactement une décision parmi les options. Adapté pour les dilemmes ou bifurcations narratives importantes où une seule voie est possible.",
    tips: [
      "Gardez 2 à 4 options pour rester lisible.",
      "Chaque décision peut avoir des conséquences différentes sur la pile.",
    ],
  },
  score: {
    label: "Page de scores / tableau de bord",
    color: "#10b981",
    icon: "📊",
    short: "Montrer où en sont les indicateurs, en graphiques.",
    description:
      "Affiche les métriques actuelles sous forme de graphiques. Trois types de dashboard sont disponibles : CBS (Coefficient de Biotope), Water Footprint (empreinte eau), ou les deux. Des composants graphiques supplémentaires peuvent être ajoutés (gauge, radar, carte…).",
    tips: [
      "'both' affiche CBS + Water Footprint.",
      "L'ID 'additional_graph' pointe vers un score waterfootprint complexe à décomposer.",
      "list_graph permet d'ajouter des composants spécifiques (cbs_score, radar_chart, simple_gauge…).",
    ],
  },
  command_control: {
    label: "Page commande & contrôle",
    color: "#8b5cf6",
    icon: "🎛",
    short: "Réduire une empreinte, donut de suivi en temps réel.",
    description:
      "Similaire à la mosaïque, mais orientée vers la réduction d'un score complexe (waterfootprint d'un chantier, d'une exploitation…). Un graphe en donut montre l'impact cumulé des décisions sélectionnées sur le score cible en temps réel.",
    tips: [
      "'score_in_doughnut' est l'ID du score métrique affiché dans le graphe ; 'chart_scores' en montre deux.",
      "'chart_display' choisit la forme : anneau (défaut), demi-anneau ou barres.",
      "Idéal pour les phases chantier ou exploitation où on réduit une empreinte eau.",
    ],
  },
  two_doughnuts_graphs: {
    label: "Double graphe en anneau",
    color: "#ec4899",
    icon: "🍩",
    short: "Comparer deux empreintes côte à côte, en fin de phase.",
    description:
      "Affiche côte à côte deux graphes en donut pour des scores complexes avec metadata (ex: empreinte eau par lieu et par usage chez Disney, consommation Paris). Utilisé en fin de phase pour montrer l'évolution globale.",
    tips: [
      "Ne fonctionne qu'avec des scores ayant une structure metadata {standard, current}.",
      "Pas de décisions sur ce type de page : c'est une page de visualisation pure.",
    ],
  },
  territory_intro: {
    label: "Module territoire 3D",
    color: "#0ea5e9",
    icon: "🗺️",
    short: "Survoler le territoire en 3D, étape par étape.",
    description:
      "Visite guidée d'un territoire en 3D : une séquence linéaire d'étapes (message, graphique ou quizz), chacune avec sa propre vue caméra et ses propres couches cartographiques actives. Aucune décision, aucun impact sur les scores : le joueur avance avec le bouton Suivant.",
    tips: [
      "Choisissez d'abord un territoire : les couches disponibles en dépendent.",
      "Chaque étape capture sa propre vue caméra directement sur la carte 3D (bouton 'Capturer cette vue').",
      "Une étape 'Graphique' réutilise les mêmes composants qu'une page de score (radar, jauge, empreinte eau…).",
    ],
  },
  custom: {
    label: "Écran custom",
    color: "#64748b",
    icon: "🧱",
    short: "Monter un écran React écrit à la main, choisi dans une liste.",
    description:
      "Affiche un composant React dédié, nommé dans `content.component_id` et choisi parmi le registre des écrans custom. Sert aux écrans trop particuliers pour être décrits par des données : aujourd'hui les trois bilans de fin d'atelier. Ces écrans lisent l'état de la partie directement : il n'y a rien à régler ici en dehors du choix du composant.",
    tips: [
      "La liste est fermée : un écran custom s'ajoute en une entrée dans front/src/components/graph_pages/customPages.js.",
      "Vérifiez que l'atelier porte bien les scores attendus par l'écran : le validateur les exige à l'enregistrement.",
      "Un écran custom est une exception assumée : préférez une page de score et son list_graph quand c'est possible.",
    ],
  },
};

/**
 * COMMENT UNE PAGE SE PRÉSENTE dans les écrans qui en montrent beaucoup —
 * déroulé, graphe de l'atelier, carte des conséquences, sélecteurs.
 *
 * Passe par le registre des écrans custom quand il y a lieu : sans ça, toutes
 * les pages `custom` porteraient la même icône et le même libellé, et un bilan
 * BTP serait indiscernable d'un bilan Compte-Gouttes dans le graphe d'un
 * atelier. C'est le prix d'un type unique — il se paie une fois, ici.
 */
export function displayInfoOf(page) {
  const base = PAGE_TYPE_INFO[page?.type];
  if (page?.type !== "custom") return base;
  const entry = customPageById(page?.content?.component_id);
  return entry ? { ...base, label: entry.label, icon: entry.icon } : base;
}

export const GRAPH_COMPONENTS = [
  { id: "cbs_score", label: "Score CBS", metadataField: null },
  { id: "waterfootprint_score", label: "Empreinte eau (phase)", metadataField: "phase_focus", metadataLabel: "Phase (score_id)" },
  { id: "radar_chart", label: "Radar (métriques multiples)", metadataField: "selected_metrics", metadataLabel: "Métriques affichées" },
  { id: "simple_gauge", label: "Jauge simple", metadataField: "key", metadataLabel: "Score affiché" },
  { id: "actor_pannel", label: "Panneau acteurs", metadataField: null },
  { id: "actor_pannel_cg", label: "Panneau acteurs (Compte-Gouttes)", metadataField: null },
  { id: "aware_map", label: "Carte", metadataField: null },
  { id: "water_histogram", label: "Histogramme eau (avec courbe d'objectif)", metadataField: null },
];

/** Le document vide d'un type, tel qu'il est créé. */
export const emptyByType = {
  // Une page neuve naît en « diapo » : c'est ce que 168 des 187 pages de
  // contexte de la base sont, et le format se change d'un clic.
  context: {
    _id: "", type: "context", title: "", description: "", tags: [],
    content: { layout: "slide", media_url: "", media_type: "photo", text: "", audio_url: "", items: [], background_image_url: "" },
    page_general_consequence: [],
  },
  mosaic: {
    _id: "", type: "mosaic", title: "", description: "", tags: [], round: "",
    content: { list_decisions: [], background_image_url: "", display_score_decisions: [], list_additional_indicators: [] },
    constraints: [], page_general_consequence: [],
  },
  "one-choice": {
    _id: "", type: "one-choice", title: "", description: "", tags: [],
    content: { list_decisions: [], background_image_url: "", list_additional_indicators: [] },
    page_general_consequence: [],
  },
  score: {
    _id: "", type: "score", title: "", description: "", tags: [],
    content: { dashboard_type: "both", background_image_url: "", list_graph: [] },
  },
  command_control: {
    _id: "", type: "command_control", title: "", description: "", tags: [],
    content: { list_decisions: [], background_image_url: "", score_in_doughnut: "", display_score_decisions: [], list_additional_indicators: [] },
    constraints: [], page_general_consequence: [],
  },
  two_doughnuts_graphs: {
    _id: "", type: "two_doughnuts_graphs", title: "", description: "", tags: [],
    content: { list_scores: [], background_img_url: "" },
    page_general_consequence: [],
  },
  territory_intro: {
    _id: "", type: "territory_intro", title: "", description: "", tags: [],
    content: { territory_id: "", layers: [], fog_enabled: true, steps: [] },
  },
  custom: {
    _id: "", type: "custom", title: "", description: "", tags: [],
    // `metadata` est le chemin de sortie du durcissement des écrans custom
    // (cf. customPages.js) : présent dès la création, vide tant qu'aucun
    // composant ne lit de réglage.
    content: { component_id: "", metadata: {} },
  },
};

/** Les écrans custom montables, pour le sélecteur du composeur. */
export const CUSTOM_PAGE_OPTIONS = CUSTOM_PAGES.map(({ id, label, icon, hint }) => ({
  id, label, icon, hint,
}));

/**
 * L'IMAGE DE FOND N'A PAS LE MÊME NOM PARTOUT. Le double anneau lit
 * `background_img_url` quand tous les autres lisent `background_image_url`.
 * C'est une irrégularité des documents existants, pas un choix : on la nomme
 * ici une fois plutôt que de la deviner à chaque écran.
 */
export function backgroundFieldOf(type) {
  return type === "two_doughnuts_graphs" ? "background_img_url" : "background_image_url";
}

/* ─── Onglets du composeur ────────────────────────────────────────────
   Le même vocabulaire que l'éditeur d'étape : une barre de sections
   numérotées, dans l'ordre où l'on présente une page — ce qu'elle est, son
   décor, ce qu'on y met, ce qu'on en mesure, et ce qu'elle déclenche ensuite.
*/
export const PAGE_TABS = [
  { id: "identity", label: "Fiche", hint: "Identifiant, titre, tags, ateliers utilisateurs." },
  { id: "background", label: "Fond", hint: "Le décor de la page : image, carte du territoire." },
  { id: "content", label: "Contenu", hint: "Ce que la page montre et ce qu'elle propose." },
  { id: "scores", label: "Scores", hint: "Les mesures affichées sur la page, et la synthèse de leurs impacts." },
  { id: "consequences", label: "Conséquences", hint: "Ce que cette page et ses mesures ajoutent au parcours." },
];

/**
 * QUELS ONGLETS POUR CE TYPE. Un onglet absent vaut mieux qu'un onglet vide :
 * une page de score n'a pas de décisions à mesurer, un module d'intro pilote
 * lui-même sa carte, et seules les pages qui renvoient réellement
 * `page_general_consequence` au serveur (cf. les composants de jeu) ont des
 * suites à déclarer.
 */
const TABS_BY_TYPE = {
  context: ["identity", "background", "content", "consequences"],
  mosaic: ["identity", "background", "content", "scores", "consequences"],
  "one-choice": ["identity", "background", "content", "scores", "consequences"],
  command_control: ["identity", "background", "content", "scores", "consequences"],
  score: ["identity", "background", "content"],
  two_doughnuts_graphs: ["identity", "background", "content", "consequences"],
  // Un écran custom porte son décor et ses scores dans son propre JSX : il n'y
  // a que le composant à choisir, et la fiche de la page.
  custom: ["identity", "content"],
};

/**
 * LE MODULE TERRITOIRE A DEUX NIVEAUX D'OBJET, et sa barre le dit.
 *
 * Une page ordinaire se règle à un seul niveau : c'est la page qu'on édite. Un
 * module, lui, est une SÉQUENCE : ce qu'on retouche quinze fois par heure,
 * c'est l'étape ouverte ; ce qu'on règle une fois pour toutes, c'est le module
 * (son territoire, ses couches) et sa fiche. Les onglets suivent cette
 * fréquence plutôt que la hiérarchie : les sections de l'étape occupent le
 * rang principal et sont numérotées, les deux onglets de la page sont posés à
 * droite, derrière un séparateur, sans numéro — ils ne font pas partie de la
 * séquence de travail.
 *
 * C'est ce qui permet d'ARRIVER DIRECTEMENT DANS L'ÉDITEUR : le premier onglet
 * est celui de l'étape. Il n'y a plus d'écran intermédiaire montrant un aperçu
 * qu'on ne peut pas modifier et une liste dont le seul geste utile était
 * « ouvrir l'étape ».
 */
const TERRITORY_TABS = [
  { id: "step:type", label: "Type", group: "step", hint: "La forme de l'étape ouverte, et le chapitre dont elle parle." },
  { id: "step:frame", label: "Cadre", group: "step", hint: "Où le contenu se pose sur l'écran, et le cadrage de la caméra." },
  { id: "step:map", label: "Carte", group: "step", hint: "Les couches allumées à cette étape." },
  { id: "step:content", label: "Contenu", group: "step", hint: "Ce que l'étape raconte : texte, diapo, graphiques, quizz." },
  { id: "module", label: "Module", group: "page", hint: "Territoire, brouillard, couches disponibles, ordre des étapes." },
  { id: "identity", label: "Fiche", group: "page", hint: "Identifiant, titre, tags, ateliers utilisateurs." },
];

export function tabsForType(type) {
  if (type === "territory_intro") {
    let rank = 0;
    return TERRITORY_TABS.map((tab) => ({
      ...tab,
      index: tab.group === "step" ? String(++rank) : null,
    }));
  }
  const ids = TABS_BY_TYPE[type] || TABS_BY_TYPE.context;
  return PAGE_TABS.filter((t) => ids.includes(t.id))
    .map((tab, i) => ({ ...tab, index: String(i + 1), group: "page" }));
}

/** La section d'étape désignée par un onglet, ou `null` si l'onglet est celui de la page. */
export function stepSectionOf(tabId) {
  return tabId?.startsWith("step:") ? tabId.slice(5) : null;
}

/** Les types qui proposent une liste de décisions. */
export function hasDecisionList(type) {
  return ["mosaic", "one-choice", "command_control"].includes(type);
}
